import { useState, memo } from 'react'
import { useTranslation } from 'react-i18next'
import { ErrorResponse } from '@ocean/api'
import { useBreakpoint } from '@ocean/hooks'
import { Table, hooks } from '@ocean/tables'
import { ColumnItem } from '@ocean/tables/dist/types'
import { Mode } from '@ocean/types/dist/appearance'
import { common, errors, controls, modals } from '@ocean/ui'
import { ModalPropType } from '@ocean/ui/dist/components/modals/common/types'
import {
  TableWrapper,
  SearchWrapper,
  TableInfoWrapper,
} from 'core/people/components/common/UserPicker.styled'
import ActionsLayout, { ActionsLayoutVariant } from 'layouts/ActionsLayout'
import { namespace } from 'modules/simulatorTrainings/common/localization'
import { SpecialUser } from 'modules/simulatorTrainings/common/types'

const { Typography, Spinner } = common
const { ErrorResult, EmptyResult } = errors
const { Checkbox, Button, Search } = controls
const { Popup } = modals
const { useCheckableTable } = hooks

const I18_PATH = `${namespace}:simulatorTraining.instructorPicker`

export type InstructorPickerProps = Pick<ModalPropType, 'onClose' | 'isOpen'> & {
  isLoading?: boolean
  error?: ErrorResponse
  data?: SpecialUser[]
  addedInstructorIds?: string[]
  canBeAddedCount?: number
  onSubmit: (instructors: Record<string, SpecialUser>) => void
}

const InstructorPicker: React.FC<InstructorPickerProps> = props => {
  const { t } = useTranslation()
  const {
    data = [],
    onSubmit,
    isOpen,
    onClose,
    addedInstructorIds,
    canBeAddedCount,
    isLoading,
    error,
  } = props
  const [filteredItems, setFilteredItems] = useState<SpecialUser[]>()
  const [pageItems, setPageItems] = useState<SpecialUser[]>([])
  const isDesktop = useBreakpoint('lg')
  const {
    selectedItems: selectedInstructors,
    dirty,
    allChecked,
    toggleAllDisabled,
    getItemState,
    reset: resetCheckableTable,
    toggleAll,
    toggleItem,
  } = useCheckableTable({
    pageItems,
    checkedItemIds: addedInstructorIds,
    canBeCheckedCount: canBeAddedCount,
    disabledItemIds: addedInstructorIds,
  })

  function handleSearchSubmit(text: string) {
    if (!text) {
      setFilteredItems(data)
      return
    }
    const filteredData = data.filter(({ id, firstname, lastname, email }) =>
      `${id} ${firstname} ${lastname} ${email}`.toUpperCase().includes(text.toUpperCase())
    )
    setFilteredItems(filteredData)
  }

  function clearInstructorPicker() {
    resetCheckableTable()
    setFilteredItems(data)
  }

  function handleSubmit() {
    onClose && onClose()
    onSubmit(selectedInstructors as Record<string, SpecialUser>)
    clearInstructorPicker()
  }

  function handleClose() {
    onClose && onClose()
    clearInstructorPicker()
  }

  function renderFooter(items: SpecialUser[]) {
    return (
      <TableInfoWrapper>
        <Typography variant='small' color='grey'>
          {t(`${I18_PATH}.instructorsTable.records`)}: {items.length}
        </Typography>
      </TableInfoWrapper>
    )
  }

  function renderContent() {
    if (isLoading) return <Spinner inline />
    if (error) return <ErrorResult error={error.error} dataTest='InstructorPikerError' />
    if (!data.length)
      return (
        <EmptyResult
          title={t(`${I18_PATH}.instructorsTable.notFound`)}
          dataTest='InstructorPickerEmptyResult'
        />
      )

    const items = filteredItems || data
    return (
      <>
        <SearchWrapper>
          <Search
            placeholder={t(`${I18_PATH}.searchForInstructors`)}
            onSubmit={handleSearchSubmit}
            mode={Mode.Dark}
            dataTest='InstructorPickerSearchField'
          />
        </SearchWrapper>
        <TableWrapper>
          <Table
            data={items}
            columns={columns}
            minWidth={300}
            showPagination
            onPageChange={items => {
              setPageItems(items as SpecialUser[])
            }}
            dataTest='InstructorsTable'
            footer={renderFooter(items)}
          />
          {!items.length && (
            <EmptyResult
              title={t(`${I18_PATH}.instructorsTable.notFound`)}
              dataTest='InstructorPickerEmptyResult'
            />
          )}
        </TableWrapper>
        <ActionsLayout variant={ActionsLayoutVariant.Modal}>
          <Button
            disabled={!dirty}
            title={t(`${I18_PATH}.addAndClose`)}
            onClick={handleSubmit}
            dataTest='InstructorPickerActionButton'
          />
        </ActionsLayout>
      </>
    )
  }

  const columns: ColumnItem[] = [
    {
      title: '',
      width: '34px',
      key: 'selectInstructorCheckbox',
      valign: 'middle',
      headColRender: () => (
        <Checkbox
          disabled={toggleAllDisabled}
          checked={allChecked}
          onChange={toggleAll}
          mode={Mode.Light}
        />
      ),
      render: (item: SpecialUser) => {
        const { checked, disabled } = getItemState(item)
        return (
          <Checkbox
            disabled={disabled}
            checked={checked}
            onChange={() => toggleItem(item)}
            mode={Mode.Light}
          />
        )
      },
    },
    {
      title: t(`${I18_PATH}.instructorsTable.id`),
      key: 'userName',
      width: '20%',
      sortable: true,
      valign: 'middle',
    },
    {
      title: t(`${I18_PATH}.instructorsTable.name`),
      key: 'firstname',
      width: '20%',
      sortable: true,
      valign: 'middle',
      // TODO: Remove it later. Now firstname, lastname, email is null
      render: ({ firstname, userName }: SpecialUser) => {
        return firstname || userName
      },
    },
    {
      title: t(`${I18_PATH}.instructorsTable.surname`),
      key: 'lastname',
      width: '20%',
      sortable: true,
      valign: 'middle',
    },
    {
      title: t(`${I18_PATH}.instructorsTable.email`),
      key: 'email',
      width: '35%',
      sortable: true,
      valign: 'middle',
    },
  ]

  return (
    <Popup
      mode={Mode.Dark}
      isOpen={isOpen}
      onClose={handleClose}
      width={isDesktop ? '960px' : '900px'}
      title={t(`${I18_PATH}.title`)}
      dataTest='InstructorPickerPopup'
    >
      <>{renderContent()}</>
    </Popup>
  )
}

export default memo(InstructorPicker)
